import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import * as Dialog from '@radix-ui/react-dialog';
import type { InvitationResponse } from 'we-tree-api-client/v2';
import { useDomainStore, useFamilyStore } from '@/hooks/useStores';
import { invitationsApi } from '@/api/invitationsWrapper';
import { FamilyMemberTypeahead, type FamilyMemberOption } from '@/components/family/FamilyMemberTypeahead';
import PlanUpgradeAlert from '@/components/shared/PlanUpgradeAlert';
import { getUpgradeAwareErrorParts, isFamilyOwner } from '@/utils/familyPlan';
import './edit-invitation-modal.css';

interface EditInvitationModalProps {
  familyId: string;
  invitation: InvitationResponse | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const getUnclaimedFamilyMembers = (familyStore: ReturnType<typeof useFamilyStore>): FamilyMemberOption[] => {
  const peopleArray = Array.from(familyStore.people.values()).filter(
    (person) => !person.isPlaceholder && !person.userId
  );
  return peopleArray
    .sort((a, b) => {
      const lastNameCompare = (a.lastName || '').localeCompare(b.lastName || '');
      if (lastNameCompare !== 0) {
        return lastNameCompare;
      }
      return (a.firstName || '').localeCompare(b.firstName || '');
    })
    .map((person) => {
      const age = person.age;
      let birthYear: string | null = null;
      if (person.birthDate) {
        try {
          const birthDateObj = new Date(person.birthDate);
          if (!isNaN(birthDateObj.getTime())) {
            birthYear = birthDateObj.getFullYear().toString();
          }
        } catch {
          // Invalid date
        }
      }

      let spouseName: string | null = null;
      if (person.spouseFamilyMemberIds.length > 0) {
        const firstSpouseId = person.selectedSpouseFamilyMemberId || person.spouseFamilyMemberIds[0];
        const spouse = familyStore.getPerson(firstSpouseId);
        if (spouse) {
          spouseName = spouse.fullName;
        }
      }

      return {
        familyMemberId: person.familyMemberId,
        fullName: person.fullName,
        firstName: person.firstName,
        lastName: person.lastName,
        age,
        birthYear,
        spouseName
      };
    });
};

const EditInvitationModal: React.FC<EditInvitationModalProps> = observer(
  ({ familyId, invitation, isOpen, onClose, onSuccess }) => {
    const { t } = useTranslation('invite');
    const { t: tCommon } = useTranslation('common');
    const domainStore = useDomainStore();
    const familyStore = useFamilyStore();
    const [email, setEmail] = useState('');
    const [familyMemberId, setFamilyMemberId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [upgradeHint, setUpgradeHint] = useState<string | null>(null);
    const [priorInviteCancelled, setPriorInviteCancelled] = useState(false);

    const isOwner = domainStore.selectedFamilyId === familyId && isFamilyOwner(domainStore.currentFamily?.userRole);
    const unclaimedMembers = getUnclaimedFamilyMembers(familyStore);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open) onClose();
      },
      [onClose]
    );

    useEffect(() => {
      if (isOpen && invitation) {
        setEmail(invitation.email);
        setFamilyMemberId(invitation.familyMemberId || '');
        setError(null);
        setUpgradeHint(null);
        setPriorInviteCancelled(false);
      }
    }, [isOpen, invitation]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!invitation) return;

      setError(null);
      setUpgradeHint(null);

      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        setError(t('emailRequired'));
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmedEmail)) {
        setError(t('emailInvalid'));
        return;
      }

      const nextFamilyMemberId = familyMemberId || undefined;
      const prevFamilyMemberId = invitation.familyMemberId || undefined;
      const emailUnchanged = trimmedEmail.toLowerCase() === invitation.email.toLowerCase();
      const memberUnchanged = nextFamilyMemberId === prevFamilyMemberId;

      if (!priorInviteCancelled && emailUnchanged && memberUnchanged) {
        onClose();
        return;
      }

      setIsSubmitting(true);
      try {
        if (!priorInviteCancelled) {
          const cancelResult = await invitationsApi.cancelInvitation(invitation.id);
          if (!cancelResult.success) {
            setError(cancelResult.error?.getUserMessage() || t('failedCancelPrevious'));
            return;
          }
          setPriorInviteCancelled(true);
        }

        const createResult = await invitationsApi.createInvitation({
          familyId,
          email: trimmedEmail,
          familyMemberId: nextFamilyMemberId,
          role: invitation.role
        });

        if (createResult.success) {
          onSuccess();
          onClose();
        } else {
          // Prior invite was already cancelled — refresh so the list matches server state
          onSuccess();
          const parts = getUpgradeAwareErrorParts(createResult.error, isOwner, t('failedCreateAfterCancel'));
          setError(parts.message);
          setUpgradeHint(parts.upgradeHint);
        }
      } catch (err) {
        setError(t('failedUpdateRetry'));
        // eslint-disable-next-line no-console
        console.error('Edit invitation error:', err);
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
            <Dialog.Content
              className="v01-modal v01-modal--panel"
              aria-labelledby="edit-invitation-title"
              aria-describedby="edit-invitation-desc"
            >
              <div className="v01-modal__header v01-modal__header--compact">
                <h2 id="edit-invitation-title" className="v01-modal__title">
                  {t('editTitle')}
                </h2>
              </div>
              <p id="edit-invitation-desc" className="v01-modal__desc">
                {t('editDescription')}
              </p>
              <form onSubmit={handleSubmit} className="edit-invitation-modal-form">
                {error && (
                  <PlanUpgradeAlert
                    message={error}
                    upgradeHint={upgradeHint}
                    onDismiss={() => {
                      setError(null);
                      setUpgradeHint(null);
                    }}
                    className="edit-invitation-modal-error"
                  />
                )}

                <label htmlFor="edit-invite-email" className="v01-modal__label">
                  {t('emailLabel')}
                </label>
                <input
                  id="edit-invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isSubmitting}
                  className="v01-modal__input"
                  autoFocus
                />

                {unclaimedMembers.length > 0 && (
                  <div className="edit-invitation-modal-member">
                    <FamilyMemberTypeahead
                      id="edit-invite-family-member"
                      value={familyMemberId}
                      onChange={setFamilyMemberId}
                      members={unclaimedMembers}
                      disabled={isSubmitting}
                      placeholder={t('linkMemberPlaceholder')}
                      label={t('linkMemberLabel')}
                      helpText={t('linkMemberHelp')}
                    />
                  </div>
                )}

                <div className="edit-invitation-modal-actions">
                  <button type="button" onClick={onClose} className="btn btn--ghost" disabled={isSubmitting}>
                    {tCommon('cancel')}
                  </button>
                  <button type="submit" className="btn btn--primary" disabled={isSubmitting || !email.trim()}>
                    {isSubmitting ? tCommon('saving') : tCommon('save')}
                  </button>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

export default EditInvitationModal;
